import React, { useState, useRef, useEffect } from 'react';

interface HeightInputProps {
  className?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
}

export const HeightInput = ({ className, value, onChange, placeholder = 'Enter height' }: HeightInputProps) => {
  const formatHeight = (input: string) => {
    // Remove all non-digit characters
    const digits = input.replace(/[^\d]/g, '');

    if (digits.length === 0) return '';
    if (digits.length === 1) return `${digits}'`;
    if (digits.length === 2) return `${digits[0]}'${digits[1]}"`;
    if (digits.length >= 3) {
      const feet = digits.slice(0, -2);
      const inchesRaw = digits.slice(-2);

      // if inches start with 0 and have another digit,
      // treat the non-zero digit as the actual inches (e.g., "605" -> "6'5"")
      let inches = inchesRaw;
      if (inchesRaw.length === 2 && inchesRaw[0] === '0' && inchesRaw[1] !== '0') {
        inches = inchesRaw[1];
      }

      return `${feet}'${inches}"`;
    }

    return digits;
  };

  const [displayValue, setDisplayValue] = useState(() => formatHeight(value));
  const inputRef = useRef(null);
  const lastCursorPosition = useRef(0);

  // Update displayValue when value prop changes (e.g., from scrolling)
  useEffect(() => {
    const formatted = formatHeight(value);
    setDisplayValue(formatted);
  }, [value]);

  const parseHeight = (formatted: string) => {
    // Extract just the digits for the actual value
    return formatted.replace(/[^\d]/g, '');
  };

  const getCursorPositionInDigits = (cursorPos: number, formattedValue: string) => {
    // Convert cursor position in formatted string to position in digits-only string
    let digitPos = 0;
    for (let i = 0; i < cursorPos && i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        digitPos++;
      }
    }
    return digitPos;
  };

  const getCursorPositionInFormatted = (digitPos: number, formattedValue: string) => {
    // Convert digit position back to formatted string position
    let currentDigitPos = 0;
    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        if (currentDigitPos === digitPos) return i;
        currentDigitPos++;
      }
    }
    return formattedValue.length;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const cursorPos = e.target.selectionStart || 0;

    const digitsOnly = input.replace(/[^\d]/g, '');
    if (digitsOnly.length === 0) {
      setDisplayValue('');
      if (onChange) {
        onChange('');
      }
      return;
    }

    // Store cursor position relative to digits in the current input
    const digitPos = getCursorPositionInDigits(cursorPos, input);

    const formatted = formatHeight(input);
    const parsed = parseHeight(formatted);

    setDisplayValue(formatted);

    // Restore cursor position
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = getCursorPositionInFormatted(digitPos, formatted);
        (inputRef.current as HTMLInputElement).setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);

    // Call parent onChange with clean numeric value
    if (onChange) {
      onChange(parsed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow all control keys and navigation
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    // Handle Enter key
    if (e.key === 'Enter' || e.keyCode === 13) {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }

    // Special handling for backspace
    if (e.key === 'Backspace') {
      const input = e.currentTarget;
      const cursorPos = input.selectionStart || 0;
      const cursorEnd = input.selectionEnd || 0;

      // If there's a selection, let default behavior handle it
      if (cursorPos !== cursorEnd) return;

      // If cursor is at the beginning, prevent backspace
      if (cursorPos === 0) {
        e.preventDefault();
        return;
      }

      // If we're about to delete a formatting character, delete the digit before it instead
      const charBefore = displayValue[cursorPos - 1];
      if (charBefore && (charBefore === "'" || charBefore === '"')) {
        e.preventDefault();
        // Find the last digit and remove it
        const digitsOnly = displayValue.replace(/[^\d]/g, '');
        if (digitsOnly.length > 0) {
          const newDigits = digitsOnly.slice(0, -1);
          const formatted = formatHeight(newDigits);
          setDisplayValue(formatted);
          if (onChange) {
            onChange(newDigits);
          }
          setTimeout(() => {
            if (inputRef.current) {
              const newPos = formatted.length;
              (inputRef.current as HTMLInputElement).setSelectionRange(newPos, newPos);
            }
          }, 0);
        }
        return;
      }
    }

    // Allow special keys
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'PageUp',
      'PageDown',
    ];

    if (allowedKeys.includes(e.key)) return;

    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleSelect = (e: React.MouseEvent<HTMLInputElement>) => {
    lastCursorPosition.current = (e.target as HTMLInputElement).selectionStart || 0;
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onSelect={handleSelect}
      placeholder={placeholder}
      className={className}
      maxLength={6} // Limits to something like "10'11""
    />
  );
};
